import React, { useState, useEffect } from 'react';
import { Plus, BookOpen, Calendar, GraduationCap, CheckCircle, Edit, Trash2, Award, Building, Star } from 'lucide-react';
import { staffAPI } from '../services/api';
import AdvancedFilters from '../components/UI/AdvancedFilters';
import SelfServiceListSkeleton from '../components/UI/SelfServiceListSkeleton';
import Modal from '../components/UI/Modal';
import toast from '../utils/toast';
import ConfirmModal from '../components/UI/ConfirmModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import FormSelect from '../components/UI/FormSelect';

const Education = () => {
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    institution_name: '',
    qualification_type: '',
    field_of_study: '',
    degree_name: '',
    start_date: '',
    end_date: '',
    gpa: '',
    gpa_scale: '4.0',
    grade: '',
    honors: '',
    thesis_title: '',
    supervisor: '',
    verification_status: 'pending',
    verification_documents: '',
    location: '',
    institution_type: 'university',
    is_current: false,
    major: '',
    minor: '',
    credits_completed: '',
    total_credits: ''
  });

  // Qualification types
  const qualificationTypes = [
    { value: 'high_school', label: 'High School' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'associate', label: 'Associate Degree' },
    { value: 'bachelor', label: 'Bachelor\'s Degree' },
    { value: 'master', label: 'Master\'s Degree' },
    { value: 'phd', label: 'PhD' },
    { value: 'professional', label: 'Professional Degree' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'bootcamp', label: 'Bootcamp' },
    { value: 'online_course', label: 'Online Course' },
    { value: 'other', label: 'Other' }
  ];

  // Institution types
  const institutionTypes = [
    { value: 'university', label: 'University' },
    { value: 'college', label: 'College' },
    { value: 'community_college', label: 'Community College' },
    { value: 'technical_school', label: 'Technical School' },
    { value: 'online_platform', label: 'Online Platform' },
    { value: 'professional_body', label: 'Professional Body' },
    { value: 'training_center', label: 'Training Center' },
    { value: 'other', label: 'Other' }
  ];

  const verificationStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'not_required', label: 'Not Required' }
  ];

  const gpaScales = [
    { value: '4.0', label: '4.0 Scale' },
    { value: '5.0', label: '5.0 Scale' },
    { value: '10.0', label: '10.0 Scale' },
    { value: '100', label: '100 Scale' },
    { value: 'letter', label: 'Letter Grade' },
    { value: 'pass_fail', label: 'Pass/Fail' }
  ];

  const honorsTypes = [
    { value: '', label: 'No Honors' },
    { value: 'summa_cum_laude', label: 'Summa Cum Laude' },
    { value: 'magna_cum_laude', label: 'Magna Cum Laude' },
    { value: 'cum_laude', label: 'Cum Laude' },
    { value: 'deans_list', label: 'Dean\'s List' },
    { value: 'honors', label: 'Honors' },
    { value: 'distinction', label: 'Distinction' },
    { value: 'merit', label: 'Merit' },
    { value: 'other', label: 'Other' }
  ];

  // Filter configuration
  const filterConfig = {
    searchFields: ['institution_name', 'degree_name', 'field_of_study', 'major', 'minor'],
    choiceFilters: [
      { key: 'qualification_type', label: 'Qualification Type', options: qualificationTypes },
      { key: 'institution_type', label: 'Institution Type', options: institutionTypes },
      { key: 'verification_status', label: 'Verification Status', options: verificationStatuses },
      { key: 'honors', label: 'Honors', options: honorsTypes }
    ],
    dateFilters: [
      { key: 'start_date', label: 'Start Date Range' },
      { key: 'end_date', label: 'End Date Range' }
    ],
    booleanFilters: [
      { key: 'is_current', label: 'Currently Studying' },
      { key: 'has_thesis', label: 'Has Thesis' },
      { key: 'has_honors', label: 'Has Honors' }
    ],
    rangeFilters: [
      { key: 'gpa', label: 'GPA Range', min: 0, max: 4.0, step: 0.1 },
      { key: 'duration_years', label: 'Duration (Years)', min: 0.5, max: 10, step: 0.5 }
    ]
  };

  useEffect(() => {
    fetchEducations();
    fetchStatistics();
  }, []);

  const fetchEducations = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await staffAPI.getEducations(filters);
      setEducations(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to fetch education records');
      console.error('Error fetching educations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await staffAPI.getEducationStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFilterChange = (filters) => {
    fetchEducations(filters);
  };

  const handleAddEducation = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.createEducation(formData);
      toast.success('Education record added successfully');
      setShowAddForm(false);
      resetForm();
      fetchEducations();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to add education record');
      console.error('Error adding education:', error);
    }
  };

  const handleEditEducation = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.updateEducation(editingEducation.id, formData);
      toast.success('Education record updated successfully');
      setEditingEducation(null);
      resetForm();
      fetchEducations();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to update education record');
      console.error('Error updating education:', error);
    }
  };

  const handleDeleteEducation = (id) => {
    setConfirmAction({ id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await staffAPI.deleteEducation(confirmAction.id);
      toast.success('Education record deleted successfully');
      fetchEducations();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to delete education record');
      console.error('Error deleting education:', error);
    } finally {
      setConfirmAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      institution_name: '',
      qualification_type: '',
      field_of_study: '',
      degree_name: '',
      start_date: '',
      end_date: '',
      gpa: '',
      gpa_scale: '4.0',
      grade: '',
      honors: '',
      thesis_title: '',
      supervisor: '',
      verification_status: 'pending',
      verification_documents: '',
      location: '',
      institution_type: 'university',
      is_current: false,
      major: '',
      minor: '',
      credits_completed: '',
      total_credits: ''
    });
  };

  const startEdit = (education) => {
    setEditingEducation(education);
    setFormData({
      institution_name: education.institution_name || '',
      qualification_type: education.qualification_type || '',
      field_of_study: education.field_of_study || '',
      degree_name: education.degree_name || '',
      start_date: education.start_date || '',
      end_date: education.end_date || '',
      gpa: education.gpa || '',
      gpa_scale: education.gpa_scale || '4.0',
      grade: education.grade || '',
      honors: education.honors || '',
      thesis_title: education.thesis_title || '',
      supervisor: education.supervisor || '',
      verification_status: education.verification_status || 'pending',
      verification_documents: education.verification_documents || '',
      location: education.location || '',
      institution_type: education.institution_type || 'university',
      is_current: education.is_current || false,
      major: education.major || '',
      minor: education.minor || '',
      credits_completed: education.credits_completed || '',
      total_credits: education.total_credits || ''
    });
    setShowAddForm(true);
  };

  const getQualificationColor = (type) => {
    const colors = {
      high_school: 'bg-blue-100 text-blue-800',
      diploma: 'bg-green-100 text-green-800',
      associate: 'bg-purple-100 text-purple-800',
      bachelor: 'bg-orange-100 text-orange-800',
      master: 'bg-red-100 text-red-800',
      phd: 'bg-indigo-100 text-indigo-800',
      professional: 'bg-pink-100 text-pink-800',
      certificate: 'bg-yellow-100 text-yellow-800',
      bootcamp: 'bg-teal-100 text-teal-800',
      online_course: 'bg-cyan-100 text-cyan-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.other;
  };

  const getGPAColor = (gpa, scale) => {
    if (!gpa || !scale) return 'text-gray-600';
    
    const percentage = (parseFloat(gpa) / parseFloat(scale)) * 100;
    
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-blue-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return 'N/A';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const years = Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 365)));
    return `${years} year${years !== 1 ? 's' : ''}`;
  };

  const StatisticsCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SelfServiceListSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Education</h1>
        <p className="text-gray-600">Track your academic achievements and qualifications</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <StatisticsCard
          title="Total Records"
          value={statistics.total_records || 0}
          icon={BookOpen}
          color="blue"
        />
        <StatisticsCard
          title="Verified"
          value={statistics.verified_records || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatisticsCard
          title="Average GPA"
          value={statistics.average_gpa || 'N/A'}
          icon={Star}
          color="yellow"
        />
        <StatisticsCard
          title="Institutions"
          value={statistics.unique_institutions || 0}
          icon={Building}
          color="purple"
        />
      </div>

      {/* Advanced Filters */}
      <div className="mb-6">
        <AdvancedFilters
          config={filterConfig}
          onFilterChange={handleFilterChange}
          resultCount={educations.length}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Education
        </button>
      </div>

      {/* Education Records */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {educations.map(education => (
          <div key={education.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {education.degree_name || education.field_of_study}
                </h3>
                <p className="text-blue-600 font-medium mb-1">{education.institution_name}</p>
                <p className="text-sm text-gray-600 mb-2">{education.major}{education.minor && ` • Minor: ${education.minor}`}</p>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getQualificationColor(education.qualification_type)}`}>
                    {education.qualification_type}
                  </span>
                  <StatusBadge
                    status={education.verification_status === 'not_required' ? 'waived' : education.verification_status}
                    label={education.verification_status === 'not_required' ? 'Not Required' : undefined}
                  />
                  {education.is_current && (
                    <StatusBadge status="active" label="Current" />
                  )}
                  {education.honors && (
                    <StatusBadge
                      status="pending"
                      label={honorsTypes.find(h => h.value === education.honors)?.label || education.honors}
                    />
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(education)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteEducation(education.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                <span>{formatDate(education.start_date)} - {formatDate(education.end_date)}</span>
              </div>
              <div className="flex items-center">
                <GraduationCap className="w-4 h-4 mr-2 text-gray-400" />
                <span>{calculateDuration(education.start_date, education.end_date)}</span>
              </div>
              {education.gpa && (
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-2 text-gray-400" />
                  <span className={getGPAColor(education.gpa, education.gpa_scale)}>
                    GPA: {education.gpa}/{education.gpa_scale}
                  </span>
                </div>
              )}
              {education.location && (
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-2 text-gray-400" />
                  <span>{education.location}</span>
                </div>
              )}
            </div>

            {education.thesis_title && (
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-1">Thesis</h4>
                <p className="text-sm text-gray-600">{education.thesis_title}</p>
                {education.supervisor && (
                  <p className="text-sm text-gray-500">Supervisor: {education.supervisor}</p>
                )}
              </div>
            )}

            {(education.credits_completed && education.total_credits) && (
              <div className="mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">Progress</span>
                  <span className="text-sm text-gray-600">
                    {education.credits_completed}/{education.total_credits} credits
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${(education.credits_completed / education.total_credits) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {educations.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No education records found</h3>
          <p className="text-gray-600">Add your first education record to get started</p>
        </div>
      )}

      {/* Add/Edit Education Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={() => { setShowAddForm(false); setEditingEducation(null); resetForm(); }}
        title={editingEducation ? 'Edit Education Record' : 'Add Education Record'}
        size="lg"
        theme="employee"
      >
            <form onSubmit={editingEducation ? handleEditEducation : handleAddEducation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Name *
                  </label>
                  <input
                    type="text"
                    value={formData.institution_name}
                    onChange={(e) => setFormData({...formData, institution_name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qualification Type *
                  </label>
                  <FormSelect
                    options={[
                      { value: '', label: 'Select qualification type' },
                      ...qualificationTypes.map(type => ({ value: type.value, label: type.label }))
                    ]}
                    value={formData.qualification_type}
                    onChange={(val) => setFormData({...formData, qualification_type: val})}
                    theme="employee"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Degree/Program Name *
                  </label>
                  <input
                    type="text"
                    value={formData.degree_name}
                    onChange={(e) => setFormData({...formData, degree_name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field of Study *
                  </label>
                  <input
                    type="text"
                    value={formData.field_of_study}
                    onChange={(e) => setFormData({...formData, field_of_study: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Major
                  </label>
                  <input
                    type="text"
                    value={formData.major}
                    onChange={(e) => setFormData({...formData, major: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minor
                  </label>
                  <input
                    type="text"
                    value={formData.minor}
                    onChange={(e) => setFormData({...formData, minor: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Type
                  </label>
                  <FormSelect
                    options={institutionTypes.map(type => ({ value: type.value, label: type.label }))}
                    value={formData.institution_type}
                    onChange={(val) => setFormData({...formData, institution_type: val})}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                    disabled={formData.is_current}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GPA
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.gpa}
                    onChange={(e) => setFormData({...formData, gpa: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GPA Scale
                  </label>
                  <FormSelect
                    options={gpaScales.map(scale => ({ value: scale.value, label: scale.label }))}
                    value={formData.gpa_scale}
                    onChange={(val) => setFormData({...formData, gpa_scale: val})}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grade/Result
                  </label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Honors
                  </label>
                  <FormSelect
                    options={honorsTypes.map(honor => ({ value: honor.value, label: honor.label }))}
                    value={formData.honors}
                    onChange={(val) => setFormData({...formData, honors: val})}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credits Completed
                  </label>
                  <input
                    type="number"
                    value={formData.credits_completed}
                    onChange={(e) => setFormData({...formData, credits_completed: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Credits
                  </label>
                  <input
                    type="number"
                    value={formData.total_credits}
                    onChange={(e) => setFormData({...formData, total_credits: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Status
                  </label>
                  <FormSelect
                    options={verificationStatuses.map(status => ({ value: status.value, label: status.label }))}
                    value={formData.verification_status}
                    onChange={(val) => setFormData({...formData, verification_status: val})}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thesis Supervisor
                  </label>
                  <input
                    type="text"
                    value={formData.supervisor}
                    onChange={(e) => setFormData({...formData, supervisor: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thesis Title
                  </label>
                  <input
                    type="text"
                    value={formData.thesis_title}
                    onChange={(e) => setFormData({...formData, thesis_title: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_current}
                      onChange={(e) => setFormData({...formData, is_current: e.target.checked, end_date: e.target.checked ? '' : formData.end_date})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Currently studying</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingEducation(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingEducation ? 'Update' : 'Add'} Education
                </button>
              </div>
            </form>
      </Modal>

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Education Record"
        message="Are you sure you want to delete this education record?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="employee"
      />
    </div>
  );
};

export default Education; 